// URL search-param shape for DetailView state. Persists the user's
// just-viewed layer + drive time + AGB year + native-view flag + active
// scenario so a shared URL — or a refresh mid-walkthrough — reproduces
// the camera, panel, and scenario step they were on.
//
//   /detail/:millId?layer=aboveground_biomass&dt=60&year=2024&native=1
//   /detail/:millId?scenario=carbon_offense&step=2
//
// Each param is validated on read; malformed / unknown values fall
// through to the supplied default rather than crashing the panel.
import { DataLayerId, SupplyShedDriveTime } from './types';
import { DATA_LAYERS } from './dataLayers';
import { API_CONFIG } from './apiConfig';
import { findScenarioById } from './scenarios';

export interface DetailUrlState {
  layer: DataLayerId | null;
  driveTime: SupplyShedDriveTime;
  agbYear: number;
  nativeViewActive: boolean;
  // Active scenario id + step index. `null` means no scenario is active.
  // The pair is parsed atomically — an unknown id or out-of-range step
  // collapses both to null rather than rendering a half-broken state.
  scenarioId: string | null;
  scenarioStep: number;
}

const PARAM_KEYS = {
  layer: 'layer',
  driveTime: 'dt',
  agbYear: 'year',
  nativeViewActive: 'native',
  scenarioId: 'scenario',
  scenarioStep: 'step',
} as const;

const isValidLayerId = (raw: string): raw is DataLayerId => raw in DATA_LAYERS;

const isValidDriveTime = (raw: string): raw is '60' | '90' =>
  raw === '60' || raw === '90';

/**
 * Parse URL search params into validated DetailView state. Each field
 * uses the supplied `defaults` value when the param is absent or fails
 * validation, so callers always get a fully-populated state object —
 * no nullable handling at the call site.
 */
export const readDetailUrlParams = (
  search: URLSearchParams,
  defaults: DetailUrlState,
): DetailUrlState => {
  const layerRaw = search.get(PARAM_KEYS.layer);
  const layer = layerRaw && isValidLayerId(layerRaw) ? layerRaw : defaults.layer;

  const dtRaw = search.get(PARAM_KEYS.driveTime);
  const driveTime = dtRaw && isValidDriveTime(dtRaw)
    ? (Number(dtRaw) as SupplyShedDriveTime)
    : defaults.driveTime;

  const yearRaw = search.get(PARAM_KEYS.agbYear);
  let agbYear = defaults.agbYear;
  if (yearRaw !== null) {
    const parsed = Number(yearRaw);
    if (
      Number.isFinite(parsed) &&
      parsed >= API_CONFIG.agbOverlay.minYear &&
      parsed <= API_CONFIG.agbOverlay.maxYear
    ) {
      agbYear = parsed;
    }
  }

  const nativeRaw = search.get(PARAM_KEYS.nativeViewActive);
  // Param absent → fall back to default (consistent with other fields).
  // Param present → only `'1'` counts as truthy; everything else is false.
  const nativeViewActive =
    nativeRaw === null ? defaults.nativeViewActive : nativeRaw === '1';

  // Scenario id + step are parsed atomically — an unknown id or an
  // out-of-range step collapses BOTH to defaults so the panel never
  // tries to render step 4 of a 3-step scenario, or a phantom scenario
  // whose steps don't exist. Step is zero-indexed in the URL just like
  // it is internally.
  let scenarioId: string | null = defaults.scenarioId;
  let scenarioStep = defaults.scenarioStep;
  const scenarioIdRaw = search.get(PARAM_KEYS.scenarioId);
  if (scenarioIdRaw !== null) {
    const match = findScenarioById(scenarioIdRaw);
    if (match) {
      scenarioId = match.id;
      const stepRaw = search.get(PARAM_KEYS.scenarioStep);
      if (stepRaw !== null) {
        const parsed = Number(stepRaw);
        if (
          Number.isInteger(parsed) &&
          parsed >= 0 &&
          parsed < match.steps.length
        ) {
          scenarioStep = parsed;
        } else {
          // Step out of range — fall back to step 0 of the (valid) scenario
          // rather than dropping the scenario entirely.
          scenarioStep = 0;
        }
      } else {
        scenarioStep = 0;
      }
    }
  }

  return { layer, driveTime, agbYear, nativeViewActive, scenarioId, scenarioStep };
};

/**
 * Mutate a URLSearchParams instance to reflect the given state. Strips
 * params back to the supplied `omitWhen` defaults so the URL stays
 * clean for the common case (no `?dt=60&year=2025&native=0` noise on a
 * default view). Returns whether anything changed — callers can skip
 * `setSearchParams` calls when nothing needs to update.
 */
export const writeDetailUrlParams = (
  current: URLSearchParams,
  state: DetailUrlState,
  omitWhen: DetailUrlState,
): { params: URLSearchParams; changed: boolean } => {
  const next = new URLSearchParams(current);

  const setOrDelete = (key: string, value: string | null) => {
    if (value === null) {
      next.delete(key);
    } else {
      next.set(key, value);
    }
  };

  setOrDelete(
    PARAM_KEYS.layer,
    state.layer !== null && state.layer !== omitWhen.layer ? state.layer : null,
  );
  setOrDelete(
    PARAM_KEYS.driveTime,
    state.driveTime !== omitWhen.driveTime ? String(state.driveTime) : null,
  );
  setOrDelete(
    PARAM_KEYS.agbYear,
    state.agbYear !== omitWhen.agbYear ? String(state.agbYear) : null,
  );
  setOrDelete(
    PARAM_KEYS.nativeViewActive,
    state.nativeViewActive ? '1' : null,
  );
  // Scenario pair: write together when active, strip together when not.
  // step=0 is omitted as a clean-URL nicety — a /detail link with just
  // ?scenario=foo opens at step 0 and matches the in-app start state.
  setOrDelete(
    PARAM_KEYS.scenarioId,
    state.scenarioId !== null ? state.scenarioId : null,
  );
  setOrDelete(
    PARAM_KEYS.scenarioStep,
    state.scenarioId !== null && state.scenarioStep > 0
      ? String(state.scenarioStep)
      : null,
  );

  return { params: next, changed: next.toString() !== current.toString() };
};
